import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';

import client from '../../../../utils/axiosInstance';

import styles from './Introduction.module.css';

/**
 * Extracts plain text from ReactQuill HTML output
 */
const getPlainText = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

/**
 * Introduction component for the create survey page. Step 1.
 */
const Introduction = forwardRef(({
  checkForm,
  onUpdated,
  previousData = {},
  isUpdate,
  mapsLoaded,
  setMapsLoaded,
  includeLogo,
  setIncludeLogo,
  isEdit,
  showOnlyMap = false,
  quillModules,
}, ref) => {
  const [maps, setMaps] = useState([]);
  const [map, setMap] = useState(null);
  const [isValidMap, setIsValidMap] = useState('');
  const [title, setTitle] = useState('');
  const [isValidTitle, setIsValidTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isValidDescription, setIsValidDescription] = useState('');
  const [mailingMessage, setMailingMessage] = useState('@enlace@');
  const [isValidMailingMessage, setIsValidMailingMessage] = useState('');
  const [emailMask, setEmailMask] = useState('');
  const [isValidEmailMask, setIsValidEmailMask] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isValidEmailSubject, setIsValidEmailSubject] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [searchParams] = useSearchParams();
  const isMap = searchParams.get('isMap') === 'true';
  const [surveyOrMap, setSurveyOrMap] = useState(isMap ? 'map' : 'survey');
  const currentCompany = useSelector((state) => state.companies.currentCompany);

  const previousDataProcessed = useRef(false);
  const isInitialMount = useRef(true);

  const isTemplate = useCallback(() => searchParams.get('isTemplate') === 'true', [searchParams]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    switch (name) {
      case 'title':
        setTitle(value);
        setIsValidTitle('');
        break;
      case 'description':
        setDescription(value);
        setIsValidDescription('');
        break;
      case 'emailMask':
        setEmailMask(value);
        setIsValidEmailMask('');
        break;
      case 'emailSubject':
        setEmailSubject(value);
        setIsValidEmailSubject('');
        break;
      case 'mailingMessage':
        setMailingMessage(value);
        setIsValidMailingMessage('');
        break;
      case 'surveyOrMap':
        setSurveyOrMap(value);
        break;
      default:
        break;
    }
  };

  const handleChangeLogo = (event) => {
    setIncludeLogo(event.target.value === 'si');
  };

  const handleChangeMap = (newValue) => {
    setMap(newValue);
    setIsValidMap('');
  };

  const fetchMaps = useCallback(async () => {
    if (!currentCompany?.id || (mapsLoaded && maps.length > 0)) return;

    try {
      const { data } = await client.get(`GetJorneyMapCompany/${currentCompany.id}`);
      const formattedMaps = data.map((item) => ({
        ...item,
        label: item.mapJourney,
      }));
      setMaps(formattedMaps);
      setMapsLoaded(true);
    } catch (error) {
      console.error('Error fetching maps:', error);
      setMapsLoaded(true);
    }
  }, [currentCompany?.id, mapsLoaded, maps.length, setMapsLoaded]);

  const validateForm = useCallback(() => {
    let formIsValid = true;

    setIsValidMap('');
    setIsValidTitle('');
    setIsValidDescription('');
    if (!showOnlyMap) {
      setIsValidMailingMessage('');
      setIsValidEmailMask('');
      setIsValidEmailSubject('');
    }

    const titleText = getPlainText(title).trim();
    const descriptionText = getPlainText(description).trim();
    const mailingText = getPlainText(mailingMessage).trim();

    // Validate map
    const isValidSelectedMap = map && maps?.some(m => m?.id === map?.id);
    if (!isValidSelectedMap) {
      setIsValidMap('Este campo es requerido');
      formIsValid = false;
    }

    // Validate title
    if (!titleText) {
      setIsValidTitle('Este campo es requerido');
      formIsValid = false;
    } else if (titleText.length < 5) {
      setIsValidTitle('Este campo debe tener al menos 5 caracteres');
      formIsValid = false;
    } else if (titleText.length > 100) {
      setIsValidTitle('Este campo debe tener máximo 100 caracteres');
      formIsValid = false;
    }

    // Validate description — opcional
    const isDescriptionEmpty = !descriptionText || descriptionText.length === 0;
    if (!isDescriptionEmpty && descriptionText.length < 5) {
      setIsValidDescription('Este campo debe tener al menos 5 caracteres');
      formIsValid = false;
    } else if (descriptionText.length > 1000) {
      setIsValidDescription('Este campo debe tener máximo 1000 caracteres');
      formIsValid = false;
    }

    if (showOnlyMap) {
      setIsValid(formIsValid);
      return formIsValid;
    }

    // Validate mailing message
    if (mailingText.length < 5) {
      setIsValidMailingMessage('Este campo debe tener al menos 5 caracteres');
      formIsValid = false;
    } else if (!/@[^@]+@/.test(mailingText)) {
      setIsValidMailingMessage(
        'El mensaje debe contener un marcador con estructura tipo "@enlace/link@".'
      );
      formIsValid = false;
    }

    // Validate email fields when required
    const isTemplateValue = isTemplate();
    if ((!isTemplateValue || isMap) && !isUpdate) {
      if (!emailMask.trim()) {
        setIsValidEmailMask('La máscara de correo es requerido');
        formIsValid = false;
      } else if (emailMask.length < 5) {
        setIsValidEmailMask('La máscara debe tener al menos 5 carácteres');
        formIsValid = false;
      } else if (emailMask.length > 30) {
        setIsValidEmailMask('La máscara debe tener máximo 30 carácteres');
        formIsValid = false;
      }

      if (!emailSubject.trim()) {
        setIsValidEmailSubject('El asunto de correo es requerido');
        formIsValid = false;
      } else if (emailSubject.length < 5) {
        setIsValidEmailSubject('El asunto de correo debe tener al menos 5 carácteres');
        formIsValid = false;
      }
    }

    setIsValid(formIsValid);
    return formIsValid;
  }, [map, maps, showOnlyMap, title, description, mailingMessage, emailMask, emailSubject, isTemplate, isMap, isUpdate]);

  useImperativeHandle(ref, () => ({ validateForm,  getFormData: () => ({
    map,
    title,
    description,
    emailMask,
    emailSubject,
    mailingMessage,
    surveyOrMap,
    isValid,
  }) }));

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  // Load previousData when maps are ready and data changes
  useEffect(() => {
    const dataKey = previousData?.title || previousData?.map?.id || '';

    if (!previousData || Object.keys(previousData).length === 0) return;
    if (!mapsLoaded || maps.length === 0) return; // ← esperar que maps tenga datos
    if (previousDataProcessed.current === dataKey) return;

    if (previousData.map) {
      const matchingMap = typeof previousData.map === 'object'
        ? maps.find((item) => item.label === previousData.map.mapJourney)
        : maps.find((item) => item.id === previousData.map);

      if (matchingMap) {
        setMap(matchingMap);
      } else {
        // Si no encontró match pero previousData.map es objeto válido, usarlo directo
        if (typeof previousData.map === 'object' && previousData.map?.id) {
          setMap(previousData.map);
        }
      }
    }

    if (!showOnlyMap) {
      if (previousData.emailMask) setEmailMask(previousData.emailMask);
      if (previousData.emailSubject) setEmailSubject(previousData.emailSubject);
      if (previousData.mailingMessage) setMailingMessage(previousData.mailingMessage);
    }

    if (previousData.title) setTitle(previousData.title);
    if (previousData.description) setDescription(previousData.description);
    if (previousData.surveyOrMap) setSurveyOrMap(previousData.surveyOrMap);

    previousDataProcessed.current = dataKey;
  }, [previousData, mapsLoaded, maps, showOnlyMap]);

  // Notify parent on state changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  const hasPreviousData = previousData && Object.keys(previousData).length > 0;
  if (hasPreviousData && !previousDataProcessed.current) return;
  const formData = showOnlyMap
      ? { map, surveyOrMap, title, description, isValid }
      : { map, surveyOrMap, title, description, emailMask, emailSubject, mailingMessage, isValid };

    onUpdated(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map, surveyOrMap, title, description,
    ...(showOnlyMap ? [] : [emailMask, emailSubject, mailingMessage]),
    isValid, showOnlyMap,
  ]);

  // Validate when checkForm triggers
  useEffect(() => {
    if (!checkForm) return;

    const currentValidation = validateForm();

    const formData = showOnlyMap
      ? { map, surveyOrMap, title, description, isValid: currentValidation }
      : { map, surveyOrMap, title, description, emailMask, emailSubject, mailingMessage, isValid: currentValidation };

    onUpdated(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForm, map, surveyOrMap, title, description, showOnlyMap, ...(showOnlyMap ? [] : [emailMask, emailSubject, mailingMessage])]);

  const labelStyle = { marginBottom: '4px', fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)' };

  const mapAutocomplete = (
    <FormControl style={{ marginBottom: '1.5em' }}>
      <Autocomplete
        id="select-map"
        options={maps}
        clearOnEscape
        onChange={(event, newValue) => handleChangeMap(newValue)}
        value={map}
        getOptionLabel={(option) => option.label || ''}
        noOptionsText="No hay mapas disponibles"
        name="map"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Nombre del mapa"
            variant="outlined"
            error={!!isValidMap}
            helperText={isValidMap}
            name="map"
          />
        )}
        disabled={maps.length === 0 || isEdit}
      />
    </FormControl>
  );

  const titleQuill = (
    <FormControl style={{ marginBottom: '1.5em', width: '100%' }}>
      <FormLabel style={labelStyle}>Nombre de la encuesta</FormLabel>
      {isValidTitle && (
        <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 4px 14px' }}>
          {isValidTitle}
        </p>
      )}
      <ReactQuill
        theme="snow"
        value={title}
        onChange={(value) => handleChange({ target: { name: 'title', value } })}
        modules={quillModules}
        placeholder="Escribe el nombre de la encuesta..."
        style={{ backgroundColor: '#fff', borderRadius: '4px', marginBottom: '8px' }}
      />
    </FormControl>
  );

  const descriptionQuill = (
    <FormControl style={{ marginBottom: '1.5em', width: '100%' }}>
      <FormLabel style={labelStyle}>
        Propósito de la encuesta{' '}
        <span style={{ fontWeight: 'normal', fontSize: '0.75rem' }}>(opcional)</span>
      </FormLabel>
      {isValidDescription && (
        <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 4px 14px' }}>
          {isValidDescription}
        </p>
      )}
      <ReactQuill
        theme="snow"
        value={description}
        onChange={(value) => handleChange({ target: { name: 'description', value } })}
        modules={quillModules}
        placeholder="Escribe el propósito de la encuesta (opcional)..."
        style={{ backgroundColor: '#fff', borderRadius: '4px', marginBottom: '8px' }}
      />
    </FormControl>
  );

  const mailingMessageQuill = (
    <FormControl style={{ marginBottom: '1.5em', width: '100%' }}>
      <FormLabel style={labelStyle}>Mensaje de correo electrónico</FormLabel>
      {isValidMailingMessage && (
        <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 4px 14px' }}>
          {isValidMailingMessage}
        </p>
      )}
      <ReactQuill
        theme="snow"
        value={mailingMessage}
        onChange={(value) => handleChange({ target: { name: 'mailingMessage', value } })}
        modules={quillModules}
        placeholder="Escribe el mensaje que recibirá el usuario por correo..."
        style={{ backgroundColor: '#fff', borderRadius: '4px', marginBottom: '8px' }}
      />
    </FormControl>
  );

  const surveyOrMapRadio = isTemplate() && (
    <FormControl className={styles.Introduction__formControl} disabled>
      <FormLabel id="survey-or-map">Encuesta o ruta de mapa</FormLabel>
      <RadioGroup row name="surveyOrMap" value={surveyOrMap} onChange={handleChange}>
        <FormControlLabel value="survey" control={<Radio />} label="Encuesta" />
        <FormControlLabel value="map" control={<Radio />} label="Ruta de mapa" />
      </RadioGroup>
    </FormControl>
  );

  return (
    <div className={styles.form}>
      {showOnlyMap ? (
        <>
          {surveyOrMapRadio}
          {mapAutocomplete}
          {titleQuill}
          {descriptionQuill}
        </>
      ) : (
        <>
          {surveyOrMapRadio}
          {mapAutocomplete}
          {titleQuill}
          {descriptionQuill}

          {(isTemplate() && !isMap) || isUpdate ? (
            mailingMessageQuill
          ) : (
            <>
              <FormLabel>Datos correo electrónico</FormLabel>

              <FormControl style={{ marginBottom: '1.5em' }}>
                <TextField
                  id="email-mask"
                  label="Máscara correo"
                  variant="outlined"
                  value={emailMask}
                  error={!!isValidEmailMask}
                  helperText={isValidEmailMask}
                  name="emailMask"
                  fullWidth
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl style={{ marginBottom: '1.5em' }}>
                <TextField
                  id="email-subject"
                  label="Asunto correo electrónico"
                  variant="outlined"
                  value={emailSubject}
                  error={!!isValidEmailSubject}
                  helperText={isValidEmailSubject}
                  name="emailSubject"
                  fullWidth
                  onChange={handleChange}
                />
              </FormControl>

              {mailingMessageQuill}

              <FormControl className={styles.Introduction__formControl}>
                <FormLabel>¿Desea que el correo tenga el logotipo de la empresa?</FormLabel>
                <RadioGroup
                  row
                  name="includeLogo"
                  value={includeLogo ? 'si' : 'no'}
                  onChange={handleChangeLogo}
                >
                  <FormControlLabel value="si" control={<Radio />} label="Si" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </>
      )}
    </div>
  );
});

export default Introduction;